import React, { useMemo, useRef, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { ListItem, Button, Collapse } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useSelector } from 'react-redux';
import { getNotifications } from 'src/selectors/notificationsSelectors';

const useStyles = makeStyles((theme) => ({
	item: {
		display: 'block',
		paddingTop: 0,
		paddingBottom: 0,
		marginBottom: 2
	},
	itemLeaf: {
		display: 'flex',
		paddingTop: 0,
		paddingBottom: 0,
		marginBottom: 2
	},
	button: {
		padding: '11px 12px',
		justifyContent: 'flex-start',
		alignItems: 'center',
		textTransform: 'none',
		letterSpacing: 0,
		width: '100%',
		borderRadius: 6,
		color: '#444',
		fontSize: 13,
		lineHeight: 1.3,
		minHeight: 40,
		'&:hover': {
			backgroundColor: '#f5f5f5'
		}
	},
	buttonLeaf: {
		padding: '11px 12px',
		justifyContent: 'flex-start',
		alignItems: 'center',
		textTransform: 'none',
		letterSpacing: 0,
		width: '100%',
		borderRadius: 6,
		color: '#444',
		fontSize: 13,
		lineHeight: 1.3,
		minHeight: 40,
		fontWeight: theme.typography.fontWeightRegular,
		'&:hover': {
			backgroundColor: '#f5f5f5'
		},
		'&.depth-0': {
			fontWeight: 500
		}
	},
	iconSlot: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: 20,
		minWidth: 20,
		height: 20,
		marginRight: 10
	},
	icon: {
		color: '#9e9e9e',
		fontSize: '17px !important',
		width: '17px !important',
		height: '17px !important'
	},
	title: {
		flex: 1,
		minWidth: 0,
		textAlign: 'left',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	expandIcon: {
		marginLeft: 8,
		height: 16,
		width: 16,
		color: '#bdbdbd',
		flexShrink: 0
	},
	label: {
		display: 'flex',
		alignItems: 'center',
		marginLeft: 8,
		flexShrink: 0
	},
	active: {
		color: '#111',
		fontWeight: 600,
		backgroundColor: '#e4e4e4',
		'& $icon': {
			color: '#333'
		},
		'&:hover': {
			backgroundColor: '#dbdbdb'
		}
	},
	notificationsBadge: {
		marginLeft: 8,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 20,
		width: 20,
		borderRadius: '50%',
		backgroundColor: '#e0e0e0',
		fontSize: 10,
		fontWeight: 600,
		flexShrink: 0
	}
}));

function NavItem({
	title,
	href,
	depth,
	children,
	icon: Icon,
	className,
	open: openProp,
	label: Label,
	onClick,
	...rest
}) {
	const classes = useStyles();
	const [open, setOpen] = useState(openProp);
	const itemRef = useRef(null);
	const notificationsAll = useSelector(getNotifications);

	const notificationsCount = useMemo(() => {
		switch (title) {
			case 'Messages': {
				return notificationsAll.messages;
			}
			default: {
				return 0;
			}
		}
	}, [notificationsAll, title]);

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleExpandEntered = () => {
		const el = itemRef.current;
		if (!el) {
			return;
		}

		const scrollParent = el.closest('nav');
		const header = el.querySelector('button');
		if (scrollParent && header) {
			const parentRect = scrollParent.getBoundingClientRect();
			const headerRect = header.getBoundingClientRect();
			const nextTop = scrollParent.scrollTop + (headerRect.top - parentRect.top) - 8;
			scrollParent.scrollTo({
				top: Math.max(0, nextTop),
				behavior: 'smooth'
			});
			return;
		}

		el.scrollIntoView({
			behavior: 'smooth',
			block: 'start'
		});
	};

	let paddingLeft = 12;

	if (depth > 0) {
		paddingLeft = 22 + 10 * depth;
	}

	const style = {
		paddingLeft,
		paddingRight: 12
	};

	if (children) {
		return (
			<ListItem
				{...rest}
				ref={itemRef}
				className={clsx(classes.item, className)}
				disableGutters
				key={title}
			>
				<Button
					className={classes.button}
					onClick={handleToggle}
					style={style}
				>
					{Icon && (
						<span className={classes.iconSlot}>
							<Icon className={classes.icon} />
						</span>
					)}
					<span className={classes.title}>{title}</span>
					{open ? (
						<ExpandLessIcon
							className={classes.expandIcon}
							color="inherit"
						/>
					) : (
						<ExpandMoreIcon
							className={classes.expandIcon}
							color="inherit"
						/>
					)}
				</Button>
				<Collapse
					in={open}
					onEntered={handleExpandEntered}
				>
					{children}
				</Collapse>
			</ListItem>
		);
	}

	return (
		<ListItem
			{...rest}
			className={clsx(classes.itemLeaf, className)}
			disableGutters
			key={title}
		>
			<Button
				activeClassName={classes.active}
				className={clsx(classes.buttonLeaf, `depth-${depth}`)}
				component={RouterLink}
				exact
				style={style}
				to={href}
				onClick={onClick}
			>
				{Icon && (
					<span className={classes.iconSlot}>
						<Icon className={classes.icon} />
					</span>
				)}
				<span className={classes.title}>{title}</span>
				{Label && (
					<span className={classes.label}>
						<Label />
					</span>
				)}
				{notificationsCount > 0 && (
					<div className={classes.notificationsBadge}>{notificationsCount}</div>
				)}
			</Button>
		</ListItem>
	);
}

NavItem.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	depth: PropTypes.number.isRequired,
	href: PropTypes.string,
	icon: PropTypes.any,
	label: PropTypes.any,
	open: PropTypes.bool,
	title: PropTypes.string.isRequired,
	onClick: PropTypes.func
};

NavItem.defaultProps = {
	open: false
};

export default NavItem;
